import { supabase } from './supabase'

/**
 * The PPSR gate that sits between a deal and its money.
 *
 * Winnie, 11 Sep, section 4: "Committed status does not bypass the PPSR check - that gate
 * sits between deal creation and payment regardless of status or seller type, and payment
 * release must independently verify a cleared PPSR check before processing." Her
 * acceptance criterion is tighter still: "Payment cannot release for any deal, regardless
 * of seller type or status, without a cleared PPSR check recorded against that VIN."
 *
 * Two words in that do the work.
 *
 * "Independently" - the payment run already carries a tick saying somebody looked at the
 * PPSR. A tick is a person's account of what they saw. This reads the search result the
 * deal actually holds, so the two can disagree and the money still stays where it is.
 *
 * "Against that VIN" - a clear search proves nothing unless it was run on this car. A
 * search on the wrong VIN, or on a plate that belongs to another vehicle, comes back
 * clear and means nothing at all, so the VIN the search was run against is compared with
 * the VIN on the deal rather than assumed to be the same.
 */

/** The ISO VIN alphabet: I, O and Q are excluded so they cannot be read as 1 and 0. */
const VIN_PATTERN = /\b[A-HJ-NPR-Z0-9]{17}\b/g

const tidyVin = (v: unknown): string => String(v || '').toUpperCase().replace(/[^A-Z0-9]/g, '')

export interface PpsrGateResult {
  passed: boolean
  reason?: string
  /**
   * The register found finance owing. Not a refusal on its own - Direct Auto buys
   * encumbered cars all the time - but the payout letter stops being optional.
   */
  encumbered?: boolean
}

/**
 * Every VIN the stored search result mentions.
 *
 * `criteriaSummary` is what the search was run on ("VIN JTEBR3FJX0K163383"). The NEVDIS
 * block names the vehicle the register actually found. Both are collected, because a
 * search run on a plate has no VIN in the criteria but does come back with one, and a
 * match on either is a match on this car.
 */
export const vinsInPpsrDetails = (details: unknown): string[] => {
  const found = new Set<string>()

  const add = (raw: unknown) => {
    const text = String(raw || '').toUpperCase()
    const hits = text.match(VIN_PATTERN)
    if (hits) hits.forEach((h) => found.add(h))
  }

  const d = (details || {}) as Record<string, unknown>
  add(d.criteriaSummary)

  const nevdis = Array.isArray(d.nevdisVehicles) ? d.nevdisVehicles : []
  for (const v of nevdis as Array<Record<string, unknown>>) {
    if (String(v?.identifierType || '').toUpperCase() === 'VIN') add(v?.identifierValue)
  }

  return [...found]
}

/**
 * Whether money may leave for this deal.
 *
 * Never throws: a failure to read is a refusal, not an exception, because the one thing
 * this must not do is let a payment through because a query went wrong.
 */
export async function ppsrGateForPayment(dealId: string): Promise<PpsrGateResult> {
  let deal: Record<string, unknown> | null = null

  try {
    const { data } = await supabase
      .from('deals')
      .select('ppsr_status, ppsr_check_date, ppsr_details, vehicle_vin, autograb_vin')
      .eq('id', dealId)
      .maybeSingle()
    deal = (data as Record<string, unknown>) || null
  } catch {
    deal = null
  }

  if (!deal) {
    return {
      passed: false,
      reason:
        'The deal could not be read to check its PPSR result, so the payment has not been released. Try again; if it keeps failing, do not release this payment by hand.',
    }
  }

  const status = String(deal.ppsr_status || '').toLowerCase()

  /* 'unavailable' is what the search writes when it could not run at all - no VIN and
     no plate to search on, or the gateway refused. It is not a result. */
  if (!status || status === 'pending' || status === 'error' || status === 'unavailable') {
    return {
      passed: false,
      reason:
        status === 'error' || status === 'unavailable'
          ? 'The PPSR search on this vehicle did not complete, so there is no result to act on. Check the VIN or plate is recorded, run the search again from the deal, and let it finish before releasing any money.'
          : 'No PPSR search has been completed on this vehicle. The register has to be searched before money leaves, whatever the seller type and whatever stage the deal is at.',
    }
  }

  if (!deal.ppsr_check_date) {
    return {
      passed: false,
      reason:
        'The PPSR result on this deal carries no search date, so there is no evidence of when the register was searched. Run the search again before releasing payment.',
    }
  }

  const dealVin = tidyVin(deal.vehicle_vin) || tidyVin(deal.autograb_vin)
  if (!dealVin) {
    return {
      passed: false,
      reason:
        'This deal has no VIN recorded, so a PPSR result cannot be tied to the vehicle being bought. Record the VIN and run the search again before releasing payment.',
    }
  }

  const searched = vinsInPpsrDetails(deal.ppsr_details)
  if (!searched.length) {
    return {
      passed: false,
      reason:
        'The PPSR result on file does not say which vehicle was searched, so it cannot be matched to this deal. Run the search again on the VIN before releasing payment.',
    }
  }

  if (!searched.includes(dealVin)) {
    return {
      passed: false,
      reason: `The PPSR search on file was run against ${searched.join(
        ' and ',
      )}, and this deal's VIN is ${dealVin}. A clear result on another vehicle is not a clear result on this one. Run the search again on ${dealVin} before releasing payment.`,
    }
  }

  if (status === 'encumbered') {
    /* Not a refusal. The register says there is finance owing, which is a normal thing to
       buy; it is the payout letter that has to be on file, and the caller enforces it. */
    return { passed: true, encumbered: true }
  }

  if (status !== 'clear') {
    return {
      passed: false,
      reason: `The PPSR result on this deal reads "${deal.ppsr_status}", which is neither clear nor encumbered. Payment is held until the search returns a result that can be acted on.`,
    }
  }

  return { passed: true }
}
